import { getRefresh, parseJwt, refreshAccessToken } from './auth.js';

// ─────────────────────────────────────────────────────────────────────────────
// Session lifecycle — the part of authentication the dentist experiences.
//
// The mechanics (PKCE, tokens, refresh) live in auth.js and are unchanged. What
// this module adds is the answer to a question auth.js never asked: what should
// happen to the WORK IN PROGRESS when a session ends?
//
// The old answer was `redirectToLogin()` from inside bffClient. That is a full-
// document navigation: React unmounts, the DOM is destroyed, and everything not
// yet persisted goes with it. In this app that is a طرح درمان being priced, a
// tooth editor holding AI findings that were never committed to the chart, a
// نسخه mid-assembly, a SOAP note in جلسهٔ درمان — and it happened without
// warning, without confirmation, and (because the redirect lands on `/`)
// without returning the dentist to the screen they were on.
//
// The doctor app shipped exactly this failure and fixed it in bf60ad1: a doctor
// who stepped out for fifteen minutes came back, typed one more line, and
// watched the visit disappear. This app was branched before that fix and kept
// the defect. Same mechanism, same remedy.
//
// So a session ending is now an EVENT, not a navigation:
//
//   expiring — the refresh token is close to its end. Raised early, while the
//              session can still be extended silently, because the best
//              handling of an expiry is for it never to become one.
//   expired  — it is over. The app STAYS MOUNTED and raises a dialog. Nothing
//              is destroyed, the dentist sees what they were doing, and the
//              re-login is something they choose rather than something that
//              happens to them.
//
// Timing is driven by the REFRESH token, not the access token. bffClient renews
// the access token silently whenever it is stale, so its expiry is invisible
// and warning on it would fire constantly for no reason. The refresh token's
// expiry is the moment the session actually dies.
// ─────────────────────────────────────────────────────────────────────────────

export const SessionEvent = {
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
};

// How long before the end to raise the warning. Long enough to finish a
// sentence and press a button; short enough that it is not asking about
// something ten minutes away, which trains people to dismiss it.
export const EXPIRY_WARNING_MS = 120_000;

// setTimeout is clamped to a 32-bit signed millisecond count, and a longer
// delay wraps to a negative value and fires IMMEDIATELY. A Keycloak SSO session
// can legitimately be days long, so a naive `setTimeout(warnAt)` would show the
// expiry warning the instant a dentist signed in.
const MAX_TIMEOUT_MS = 2_147_483_647;

const listeners = new Set();

/** Subscribe to session lifecycle events. Returns an unsubscribe function. */
export function onSessionEvent(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Raise a session event.
 *
 * Never throws — it is called from `catch` blocks and from timers, and a
 * listener that fails must not swallow the error its caller is about to
 * re-raise, nor take the timer chain down with it.
 */
export function emitSession(event) {
  for (const listener of listeners) {
    try { listener(event); } catch { /* one broken subscriber must not stop the rest */ }
  }
}

/** Test seam. */
export function resetSessionListeners() {
  listeners.clear();
}

// ── Returning the dentist to where they were ─────────────────────────────────
// Keycloak's redirect_uri is the origin, so a re-login has always landed on
// امروز. That is a small loss on a list screen and a real one on
// /visit/:appointmentId or /patients/:id/chart — the dentist has to find the
// patient again and re-open the session they were just interrupted in.
//
// sessionStorage, not localStorage: this is a fact about THIS tab's interrupted
// journey. Two tabs re-authenticating would otherwise overwrite each other's
// destination and send one of them somewhere it never was.

const RETURN_KEY = 'parsina_return_to';

export function rememberReturnPath(path = `${window.location.pathname}${window.location.search}`) {
  try {
    // Only in-app paths. Storing an absolute URL would make this a redirect
    // parameter an attacker could aim off-site.
    if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) {
      sessionStorage.setItem(RETURN_KEY, path);
    }
  } catch { /* private mode / storage disabled — returning home is an acceptable loss */ }
}

/** Read and clear the stored destination. One-shot by design. */
export function takeReturnPath() {
  try {
    const path = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return path && path.startsWith('/') && !path.startsWith('//') ? path : null;
  } catch {
    return null;
  }
}

// ── Expiry scheduling ────────────────────────────────────────────────────────

/** Epoch ms at which the current refresh token dies, or null if unknowable. */
export function refreshTokenExpiry() {
  const refresh = getRefresh();
  if (!refresh) return null;
  const payload = parseJwt(refresh);
  // Keycloak offline tokens carry exp: 0, meaning "does not expire". Treating
  // that as "expired in 1970" would raise the dialog immediately on login.
  if (!payload?.exp) return null;
  return payload.exp * 1000;
}

/**
 * Watch the current session and raise `expiring` then `expired` at the right
 * moments. Returns a cancel function.
 *
 * Re-arms itself after a successful silent renewal rather than assuming one
 * schedule for the session's whole life: every refresh mints a new token with a
 * new expiry, so a single set of timers would go off against a token that no
 * longer exists.
 */
export function watchSession({ emit = emitSession, now = () => Date.now() } = {}) {
  let warnTimer = null;
  let endTimer = null;
  let cancelled = false;

  const clear = () => {
    clearTimeout(warnTimer);
    clearTimeout(endTimer);
    warnTimer = null;
    endTimer = null;
  };

  const arm = () => {
    clear();
    if (cancelled) return;

    const expiry = refreshTokenExpiry();
    // No refresh token, or one that never expires: nothing to schedule. An
    // actual rejection still arrives through `emitSession` from bffClient, so
    // this is not the only path to the dialog.
    if (!expiry) return;

    const remaining = expiry - now();

    if (remaining <= 0) { emit(SessionEvent.EXPIRED); return; }

    const untilWarning = remaining - EXPIRY_WARNING_MS;
    if (untilWarning > 0) {
      warnTimer = setTimeout(() => emit(SessionEvent.EXPIRING), Math.min(untilWarning, MAX_TIMEOUT_MS));
    } else {
      emit(SessionEvent.EXPIRING);
    }
    endTimer = setTimeout(() => emit(SessionEvent.EXPIRED), Math.min(remaining, MAX_TIMEOUT_MS));
  };

  arm();

  return {
    /** Re-read the token and re-arm. Call after any successful refresh. */
    rearm: arm,
    cancel: () => { cancelled = true; clear(); },
  };
}

/**
 * Extend the session on the dentist's behalf — what «ادامهٔ کار» does.
 *
 * Returns true when the session is good again. On failure the caller shows the
 * expired dialog rather than retrying: if the refresh token is dead, it will
 * still be dead a second later, and a spinner that never resolves is a worse
 * answer than an honest one.
 */
export async function extendSession() {
  const ok = await refreshAccessToken().catch(() => false);
  return Boolean(ok);
}
